/**
 * FO-421 (batch-55 bruikleen-docs) — bruikleendocumenten voor medewerker + item.
 *
 * Gedeelde request-schema's (Zod) + DTO's voor:
 *   - de bruikleen-wizard (sjabloon + medewerker + gebruiksitem → één Document),
 *   - de item-documentenlijsten (voertuig / uniek middel: "wie heeft dit in
 *     bruikleen", incl. historie na inname/overdracht),
 *   - de medewerker-lijst ("wat heeft hij in bruikleen") op de Middelen-tab.
 *
 * Eén waarheid: een bruikleendocument bestaat één keer als `Document` met de
 * medewerker als scope-eigenaar (EMPLOYEE) en het item via `DocumentKoppeling`
 * (VOERTUIG / VOORRAAD_UNIEK_ITEM). Ligt het item in het magazijn (geen
 * medewerker), dan is het item de scope-eigenaar (VOERTUIG /
 * VOORRAAD_UNIEK_ITEM). Nooit twee kopieën.
 */
import { z } from 'zod'
import type { DocumentEntiteitType } from './documenten'
import type { DocumentGeneratieWaarschuwing } from './document-templates'
import type { GeneratedDocStatus } from './client-document-templates'
// batch-62 briefpapier (FO-459) - de briefpapierkeuze bij het genereren.
import { BriefpapierKeuzeInvoerSchema, type BriefpapierToepassingDTO } from './briefpapier'

/**
 * De itemtypes die een bruikleendocument kunnen dragen. Een kar of koffer
 * (GereedschapsDrager) hangt aan zijn onderliggende uniek item, dus valt onder
 * VOORRAAD_UNIEK_ITEM - één fysiek exemplaar, één koppel-id.
 */
export const BRUIKLEEN_ITEM_TYPES = ['VOERTUIG', 'VOORRAAD_UNIEK_ITEM'] as const
export type BruikleenItemType = (typeof BRUIKLEEN_ITEM_TYPES)[number]

export const BRUIKLEEN_ITEM_TYPE_LABELS: Record<BruikleenItemType, string> = {
  VOERTUIG: 'Voertuig',
  VOORRAAD_UNIEK_ITEM: 'Middel',
}

const idRef = z.string().trim().min(1).max(64)

/** Verwijzing naar één gebruiksitem (type + id). */
export const BruikleenItemRefSchema = z.object({
  type: z.enum(BRUIKLEEN_ITEM_TYPES),
  id: idRef,
})
export type BruikleenItemRef = z.infer<typeof BruikleenItemRefSchema>

/**
 * Body voor `POST /bruikleen-documenten/genereren` en `.../preview`; dezelfde
 * velden reizen als query mee in `GET /bruikleen-documenten/auto-fill`. Een
 * medewerker en/of een item is verplicht: zonder beide zou het document geen
 * eigenaar hebben (het datalek-vangnet in de generator weigert dat óók).
 */
export const GenereerBruikleenDocumentRequestSchema = z
  .object({
    templateId: idRef,
    employeeId: idRef.nullable().optional(),
    item: BruikleenItemRefSchema.nullable().optional(),
    entityId: idRef.nullable().optional(),
    placeholderValues: z.record(z.string(), z.string()).optional(),
    /**
     * Laten ondertekenen door de medewerker? Weggelaten = de sjabloon-default
     * (`ClientDocumentTemplate.ondertekeningStandaard`). Zonder medewerker is
     * ondertekenen niet mogelijk (er is niemand die kan tekenen).
     */
    ondertekenen: z.boolean().optional(),
    /**
     * batch-57 overeenkomst-vervolg (FO-415 §6.6) — de bruikleen-taak waaruit
     * deze generatie is gestart. Wordt na het genereren afgehandeld, óók als het
     * middel niet individueel te matchen is (een set zonder unieke exemplaren).
     * Weggelaten = de dedupe gaat puur op medewerker + middel.
     */
    triggerId: idRef.nullable().optional(),
    /** FO-459 (batch-62) - briefpapierkeuze uit de kiezer; leeg = cascade. */
    briefpapier: BriefpapierKeuzeInvoerSchema.nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (!val.employeeId && !val.item) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['item'],
        message: 'Kies een medewerker en/of een item: het document heeft een eigenaar nodig.',
      })
    }
    if (val.ondertekenen === true && !val.employeeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ondertekenen'],
        message: 'Ondertekenen vereist een medewerker.',
      })
    }
  })
export type GenereerBruikleenDocumentRequest = z.infer<typeof GenereerBruikleenDocumentRequestSchema>

/** Query voor `GET /bruikleen-documenten/items` (item-picker in de wizard). */
export const BruikleenItemsQuerySchema = z.object({
  /** Alleen de items die deze medewerker nu in bruikleen heeft. */
  employeeId: idRef.optional(),
  /** Vrije zoekterm (kenteken, nummer, serienummer, IMEI, artikelnaam). */
  search: z.string().trim().max(100).optional(),
})

/**
 * Status van een bruikleendocument zoals hij op BEIDE plekken (medewerker en
 * item) getoond wordt. Afgeleid uit `ClientGeneratedDocument.status` +
 * `Document.requiresSignature`; UPLOAD = handmatig geüpload bestand (geen
 * gegenereerde overeenkomst, dus geen ondertekenstatus).
 */
export type BruikleenDocumentStatus =
  | 'CONCEPT'
  | 'WACHT_OP_ONDERTEKENING'
  | 'ONDERTEKEND'
  | 'DEFINITIEF'
  | 'UPLOAD'

export const BRUIKLEEN_DOCUMENT_STATUS_LABELS: Record<BruikleenDocumentStatus, string> = {
  CONCEPT: 'Concept',
  WACHT_OP_ONDERTEKENING: 'Wacht op ondertekening',
  ONDERTEKEND: 'Ondertekend',
  DEFINITIEF: 'Definitief',
  UPLOAD: 'Geüpload',
}

/**
 * Vertaal de opgeslagen `GeneratedDocStatus` naar de getoonde bruikleen-status.
 * Eén plek (types), zodat item-lijst, medewerker-lijst en wizard-resultaat
 * dezelfde betekenis geven aan dezelfde rij.
 */
export function bruikleenStatusVanGenerated(status: GeneratedDocStatus): BruikleenDocumentStatus {
  switch (status) {
    case 'CONCEPT':
      return 'CONCEPT'
    case 'WACHT_OP_ONDERTEKENING':
      return 'WACHT_OP_ONDERTEKENING'
    case 'ONDERTEKEND':
      return 'ONDERTEKEND'
    case 'DEFINITIEF':
    case 'VERZONDEN':
    case 'VERLOPEN':
      return 'DEFINITIEF'
  }
}

/** Eén keuze in de item-picker van de bruikleen-wizard. */
export interface BruikleenItemOptieDTO {
  type: BruikleenItemType
  id: string
  /** Hoofdregel: "AB-123-C · Volkswagen Transporter" / "KAR1001 · Servicekar". */
  label: string
  /** Subregel: soort + identificatie (serienummer/IMEI), of null. */
  sub: string | null
  /** Huidige houder (open toewijzing / UITGEGEVEN aan), of null = magazijn. */
  huidigeHouderId: string | null
  huidigeHouderNaam: string | null
}

/**
 * Eén placeholder die vóór het genereren nog leeg is: welke, waarom leeg en of
 * hij verplicht is (T2: geen half ingevulde documenten zonder dat de gebruiker
 * het weet).
 */
export interface OntbrekendePlaceholderDTO {
  key: string
  label: string
  /** Leesbare reden ("Medewerker heeft geen adres in het dossier", ...). */
  reden: string
  required: boolean
  /** De geconfigureerde autofill-bron (`employee.adres`), of null = handmatig veld. */
  bron: string | null
}

/** Antwoord van `GET /bruikleen-documenten/auto-fill`. */
export interface BruikleenAutoFillDTO {
  values: Record<string, string>
  autoFilledKeys: string[]
  ontbrekend: OntbrekendePlaceholderDTO[]
  /**
   * Kan de gekozen medewerker digitaal ondertekenen (actief werkaccount met
   * portal-toegang)? `null` = geen medewerker gekozen. Bij `false` zet de wizard
   * "laten ondertekenen" uit en legt uit waarom: anders zou het document
   * eindeloos op "wacht op ondertekening" blijven staan zonder dat iemand het
   * kan tekenen (de API weigert zo'n generatie ook: 422 `GEEN_PORTAL_TOEGANG`).
   */
  medewerkerKanOndertekenen: boolean | null
}

/**
 * Eén document op een gebruiksitem (voertuig of uniek middel): gegenereerd
 * bruikleendocument óf handmatig geüpload bestand. Zelfde `Document`-record als
 * in het medewerkerdossier - verwijderen/vervangen daar is hier direct zichtbaar.
 */
export interface ItemDocumentRowDTO {
  documentId: string
  /** De item-koppeling (voor "ontkoppelen van dit item"); null als het document
   *  het item als scope-eigenaar heeft (magazijn-generatie: niet ontkoppelbaar). */
  koppelingId: string | null
  titel: string
  categorie: string
  /**
   * batch-68 werkplaatsfacturen (FO-341 §11.4) - het bibliotheek-documenttype
   * (o.a. WERKPLAATSFACTUUR), voor het soortfilter op de documenten-tab. Null =
   * (nog) geen type. Optioneel voor oudere fixtures.
   */
  documenttype?: string | null
  bron: 'GEGENEREERD' | 'UPLOAD'
  status: BruikleenDocumentStatus
  /** Id van de ClientGeneratedDocument-rij (download via de generated-proxy), of null bij UPLOAD. */
  generatedId: string | null
  /** Medewerker waar dit document bij hoort (scope-eigenaar), of null = alleen item. */
  employeeId: string | null
  employeeNaam: string | null
  /**
   * Historie: het document hoort bij een medewerker die het item nu NIET meer
   * in bruikleen heeft (ingenomen / overgedragen). Het document blijft bij
   * beiden zichtbaar; een nieuwe uitgifte krijgt een nieuw document.
   */
  isHistorie: boolean
  requiresSignature: boolean
  ondertekendOp: string | null
  /** Laatste versienummer (download via /api/documenten/:id/versies/:nr/download). */
  versionNr: number
  fileName: string
  fileSize: number
  mimeType: string
  uploadedAt: string
  uploadedByName: string | null
  createdAt: string
}

/** Eén bruikleendocument in de medewerker-lijst (Middelen-tab: "wat heeft hij in bruikleen"). */
export interface MedewerkerBruikleenDocumentDTO {
  generatedId: string
  documentId: string
  titel: string
  templateNaam: string
  status: BruikleenDocumentStatus
  itemType: DocumentEntiteitType | null
  itemId: string | null
  /** Leesbaar item-label ("AB-123-C · Volkswagen Transporter"), of null als het item niet (meer) bestaat. */
  itemLabel: string | null
  /** Pad naar het item-detail in de backoffice, of null. */
  itemHref: string | null
  /**
   * Het document is bij het item ontkoppeld (koppeling soft-deleted): het label
   * komt dan nog uit de herkomst-snapshot, maar er is geen levende relatie meer.
   */
  itemOntkoppeld: boolean
  requiresSignature: boolean
  ondertekendOp: string | null
  versionNr: number
  fileName: string
  createdAt: string
}

/** Resultaat van `POST /bruikleen-documenten/genereren`. */
export interface GenereerBruikleenDocumentResultDTO {
  generatedId: string
  documentId: string
  status: BruikleenDocumentStatus
  employeeId: string | null
  item: BruikleenItemRef | null
  fileName: string
  /** Versienummer van de PDF (download via /api/documenten/:id/versies/:nr/download). */
  versionNr: number
  /** FO-401 - niet-blokkerende kwaliteitswaarschuwingen (huisstijl, lege pagina). */
  waarschuwingen: DocumentGeneratieWaarschuwing[]
  /** FO-459 (batch-62) - het toegepaste briefpapier (ook vastgelegd op het document). */
  briefpapier: BriefpapierToepassingDTO
}
